import { FC, useState, useEffect } from 'react';
import { Avatar, AvatarImage, AvatarFallback } from '../ui/avatar';
import { Button } from '../ui/button';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { AlertTriangle, Flag, MoreVertical, Pencil, Trash2 } from 'lucide-react';
import { useReports } from '@/hooks/useReports';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from '@/components/ui/textarea';
import { supabase } from '@/lib/supabase';
import { useToast } from '@/components/ui/use-toast';

interface Comment {
  id: string;
  content: string;
  created_at: string;
  user_id: string;
  author: {
    student_name: string;
    avatar_url?: string;
  };
  hidden?: boolean;
  hidden_reason?: string;
}

interface CommentItemProps {
  comment: {
    id: string;
    content: string;
    created_at: string;
    user_id: string;
    author: {
      student_name: string;
      avatar_url: string | null;
    };
  };
  onCommentUpdate?: () => void;
}

export const CommentItem: FC<CommentItemProps> = ({ comment, onCommentUpdate }) => {
  const [reportReason, setReportReason] = useState('');
  const [reportDialogOpen, setReportDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editContent, setEditContent] = useState(comment.content);
  const [isAuthor, setIsAuthor] = useState(false);
  const { createReport } = useReports();
  const { toast } = useToast();

  useEffect(() => {
    const checkAuthor = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      setIsAuthor(user?.id === comment.user_id);
    };
    checkAuthor();
  }, [comment.user_id]);

  const handleReport = async () => {
    if (!reportReason) {
      toast({
        variant: "destructive",
        title: "Error",
        description: "Por favor, selecciona una razón para el reporte.",
      });
      return;
    }

    try {
      const success = await createReport({
        content_type: 'comment',
        reported_content: comment.content,
        reason: reportReason,
        reported_user_id: comment.user_id,
        content_id: comment.id,
      });

      if (success) {
        setReportDialogOpen(false);
        setReportReason('');
        toast({
          title: "Reporte enviado",
          description: "Gracias por ayudarnos a mantener la comunidad segura.",
        });
      }
    } catch (error) {
      console.error('Error reporting comment:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "No se pudo enviar el reporte. Por favor, intenta de nuevo.",
      });
    }
  };

  const handleEdit = async () => {
    if (!editContent.trim() || editContent === comment.content) {
      setIsEditing(false);
      return;
    }

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('No user found');

      const { error } = await supabase
        .from('post_comments')
        .update({ content: editContent.trim() })
        .eq('id', comment.id)
        .eq('user_id', user.id);

      if (error) throw error;

      toast({
        title: "¡Comentario actualizado!",
        description: "Tu comentario ha sido actualizado exitosamente.",
      });

      setIsEditing(false);
      if (onCommentUpdate) {
        onCommentUpdate();
      }
    } catch (error) {
      console.error('Error editing comment:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "No se pudo actualizar el comentario",
      });
    }
  };

  const handleDelete = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('No user found');

      const { error } = await supabase
        .from('post_comments')
        .delete()
        .eq('id', comment.id)
        .eq('user_id', user.id);

      if (error) throw error;

      toast({
        title: "Comentario eliminado",
        description: "Tu comentario ha sido eliminado exitosamente.",
      });

      setDeleteDialogOpen(false);
      if (onCommentUpdate) {
        onCommentUpdate();
      }
    } catch (error) {
      console.error('Error deleting comment:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "No se pudo eliminar el comentario",
      });
    }
  };

  if (comment.hidden) {
    return null;
  }

  return (
    <>
      <div className="flex items-start gap-2 bg-gray-50/50 rounded-lg p-2">
        <Avatar className="w-6 h-6 sm:w-8 sm:h-8">
          <AvatarImage src={comment.author?.avatar_url || undefined} />
          <AvatarFallback className="bg-gradient-to-br from-primary/20 to-primary/30 text-primary-foreground">
            {comment.author?.student_name?.[0] || '?'}
          </AvatarFallback>
        </Avatar>
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium text-xs sm:text-sm truncate">
                {comment.author?.student_name || 'Usuario'}
              </p>
              <p className="text-[10px] sm:text-xs text-gray-500">
                {format(new Date(comment.created_at), 'dd MMM yyyy, HH:mm', { locale: es })}
              </p>
            </div>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" className="h-6 w-6 sm:h-8 sm:w-8 text-gray-500 hover:text-gray-700">
                  <MoreVertical className="h-3 w-3 sm:h-4 sm:w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {isAuthor && (
                  <>
                    <DropdownMenuItem onSelect={() => setIsEditing(true)}>
                      <Pencil className="h-3 w-3 sm:h-4 sm:w-4 mr-2" />
                      <span className="text-xs sm:text-sm">Editar comentario</span>
                    </DropdownMenuItem>
                    <DropdownMenuItem onSelect={() => setDeleteDialogOpen(true)}>
                      <Trash2 className="h-3 w-3 sm:h-4 sm:w-4 mr-2" />
                      <span className="text-xs sm:text-sm">Eliminar comentario</span>
                    </DropdownMenuItem>
                  </>
                )}
                <DropdownMenuItem onSelect={() => setReportDialogOpen(true)}>
                  <Flag className="h-3 w-3 sm:h-4 sm:w-4 mr-2" />
                  <span className="text-xs sm:text-sm">Reportar comentario</span>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>

          {isEditing ? (
            <div className="mt-2">
              <Textarea
                value={editContent}
                onChange={(e) => setEditContent(e.target.value)}
                className="min-h-[60px] text-sm"
              />
              <div className="flex justify-end gap-2 mt-2">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setIsEditing(false);
                    setEditContent(comment.content);
                  }}
                >
                  Cancelar
                </Button>
                <Button
                  size="sm"
                  onClick={handleEdit}
                >
                  Guardar
                </Button>
              </div>
            </div>
          ) : (
            <p className="text-xs sm:text-sm text-gray-800 whitespace-pre-wrap break-words">
              {comment.content}
            </p>
          )}
        </div>
      </div>

      <Dialog open={reportDialogOpen} onOpenChange={setReportDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reportar comentario</DialogTitle>
            <DialogDescription>
              Por favor, selecciona una razón para reportar este comentario.
            </DialogDescription>
          </DialogHeader>
          <Select value={reportReason} onValueChange={setReportReason}>
            <SelectTrigger>
              <SelectValue placeholder="Selecciona una razón" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="spam">Spam</SelectItem>
              <SelectItem value="inappropriate">Contenido inapropiado</SelectItem>
              <SelectItem value="harassment">Acoso</SelectItem>
              <SelectItem value="other">Otro</SelectItem>
            </SelectContent>
          </Select>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setReportDialogOpen(false)}>
              Cancelar
            </Button>
            <Button onClick={handleReport}>
              Reportar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>¿Eliminar comentario?</DialogTitle>
            <DialogDescription>
              Esta acción no se puede deshacer. ¿Estás seguro de que deseas eliminar este comentario?
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="ghost"
              onClick={() => setDeleteDialogOpen(false)}
            >
              Cancelar
            </Button>
            <Button
              variant="destructive"
              className="bg-red-500 hover:bg-red-600 text-white"
              onClick={handleDelete}
            >
              Eliminar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
